package fr.manyverse.app.presentation.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import kotlin.math.abs

/**
 * A lightweight, dependency-free HSV colour picker that works on every Compose Multiplatform
 * target. Shows a Saturation/Value square plus a hue bar; both are draggable and tappable.
 *
 * @param color the currently selected colour (drives the cursors; external changes — e.g.
 *   picking a colour from an image — are reflected back into the picker).
 * @param onColorChanged invoked with the new colour whenever the user moves a cursor.
 */
@Composable
fun HsvColorPicker(
    color: Color,
    onColorChanged: (Color) -> Unit,
    modifier: Modifier = Modifier
) {
    var hue by remember { mutableFloatStateOf(0f) }
    var saturation by remember { mutableFloatStateOf(0f) }
    var value by remember { mutableFloatStateOf(0f) }

    // Keep internal HSV in sync with the externally-provided colour (reset / image extraction).
    // Hue is only adopted from chromatic colours, so dragging towards grey doesn't reset it.
    LaunchedEffect(color) {
        val (h, s, v) = color.toHsv()
        if (s > 0.001f) hue = h
        saturation = s
        value = v
    }

    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(12.dp)) {
        // Saturation / Value square
        Canvas(
            modifier = Modifier
                .fillMaxWidth()
                .height(180.dp)
                .clip(RoundedCornerShape(8.dp))
                .pointerInput(Unit) {
                    detectTapGestures { pos ->
                        val (s, v) = svFromOffset(pos, size)
                        saturation = s
                        value = v
                        onColorChanged(hsvToColor(hue, s, v))
                    }
                }
                .pointerInput(Unit) {
                    detectDragGestures { change, _ ->
                        val (s, v) = svFromOffset(change.position, size)
                        saturation = s
                        value = v
                        onColorChanged(hsvToColor(hue, s, v))
                    }
                }
        ) {
            val hueColor = hsvToColor(hue, 1f, 1f)
            drawRect(brush = Brush.horizontalGradient(listOf(Color.White, hueColor)))
            drawRect(brush = Brush.verticalGradient(listOf(Color.Transparent, Color.Black)))

            val cx = (saturation * size.width).coerceIn(0f, size.width)
            val cy = ((1f - value) * size.height).coerceIn(0f, size.height)
            val r = 8.dp.toPx()
            drawCircle(Color.Black, radius = r, center = Offset(cx, cy), style = Stroke(width = 3.dp.toPx()))
            drawCircle(Color.White, radius = r, center = Offset(cx, cy), style = Stroke(width = 1.5.dp.toPx()))
        }

        // Hue bar
        Canvas(
            modifier = Modifier
                .fillMaxWidth()
                .height(28.dp)
                .clip(RoundedCornerShape(14.dp))
                .pointerInput(Unit) {
                    detectTapGestures { pos ->
                        val h = hueFromOffset(pos, size)
                        hue = h
                        onColorChanged(hsvToColor(h, saturation, value))
                    }
                }
                .pointerInput(Unit) {
                    detectDragGestures { change, _ ->
                        val h = hueFromOffset(change.position, size)
                        hue = h
                        onColorChanged(hsvToColor(h, saturation, value))
                    }
                }
        ) {
            val hueColors = (0..360 step 30).map { hsvToColor(it.toFloat(), 1f, 1f) }
            drawRect(brush = Brush.horizontalGradient(hueColors))

            val tx = ((hue / 360f) * size.width).coerceIn(0f, size.width)
            val thumbW = 4.dp.toPx()
            drawRect(
                color = Color.White,
                topLeft = Offset(tx - thumbW / 2f, 0f),
                size = androidx.compose.ui.geometry.Size(thumbW, size.height),
                style = Stroke(width = 2.dp.toPx())
            )
        }
    }
}

private fun svFromOffset(pos: Offset, size: IntSize): Pair<Float, Float> {
    val s = (pos.x / size.width).coerceIn(0f, 1f)
    val v = (1f - pos.y / size.height).coerceIn(0f, 1f)
    return s to v
}

private fun hueFromOffset(pos: Offset, size: IntSize): Float =
    ((pos.x / size.width).coerceIn(0f, 1f)) * 360f

/** Builds a [Color] from HSV components (h in 0..360, s/v in 0..1). */
fun hsvToColor(h: Float, s: Float, v: Float): Color {
    val c = v * s
    val hp = (h % 360f + 360f) % 360f / 60f
    val x = c * (1f - abs(hp % 2f - 1f))
    val (r1, g1, b1) = when {
        hp < 1f -> Triple(c, x, 0f)
        hp < 2f -> Triple(x, c, 0f)
        hp < 3f -> Triple(0f, c, x)
        hp < 4f -> Triple(0f, x, c)
        hp < 5f -> Triple(x, 0f, c)
        else -> Triple(c, 0f, x)
    }
    val m = v - c
    return Color(r1 + m, g1 + m, b1 + m)
}

/** Decomposes a [Color] into HSV components: (hue 0..360, saturation 0..1, value 0..1). */
fun Color.toHsv(): Triple<Float, Float, Float> {
    val r = red
    val g = green
    val b = blue
    val max = maxOf(r, g, b)
    val min = minOf(r, g, b)
    val d = max - min
    val h = when {
        d == 0f -> 0f
        max == r -> 60f * (((g - b) / d) % 6f)
        max == g -> 60f * (((b - r) / d) + 2f)
        else -> 60f * (((r - g) / d) + 4f)
    }.let { if (it < 0f) it + 360f else it }
    val s = if (max == 0f) 0f else d / max
    return Triple(h, s, max)
}
